import _extends from 'babel-runtime/helpers/extends';

var NAMESPACE = 'td';

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

/**
 * lodash (Custom Build) <https://lodash.com/>
 * Build: `lodash modularize exports="npm" -o ./`
 * Copyright jQuery Foundation and other contributors <https://jquery.org/>
 * Released under MIT license <https://lodash.com/license>
 * Based on Underscore.js 1.8.3 <http://underscorejs.org/LICENSE>
 * Copyright Jeremy Ashkenas, DocumentCloud and Investigative Reporters & Editors
 */

/** Used as the `TypeError` message for "Functions" methods. */
var FUNC_ERROR_TEXT = 'Expected a function';

/** Used as references for various `Number` constants. */
var NAN = 0 / 0;

/** `Object#toString` result references. */
var symbolTag = '[object Symbol]';

/** Used to match leading and trailing whitespace. */
var reTrim = /^\s+|\s+$/g;

/** Used to detect bad signed hexadecimal string values. */
var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;

/** Used to detect binary string values. */
var reIsBinary = /^0b[01]+$/i;

/** Used to detect octal string values. */
var reIsOctal = /^0o[0-7]+$/i;

/** Built-in method references without a dependency on `root`. */
var freeParseInt = parseInt;

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = freeGlobal || freeSelf || Function('return this')();

/** Used for built-in method references. */
var objectProto = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var objectToString = objectProto.toString;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax = Math.max,
    nativeMin = Math.min;

/**
 * Gets the timestamp of the number of milliseconds that have elapsed since
 * the Unix epoch (1 January 1970 00:00:00 UTC).
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Date
 * @returns {number} Returns the timestamp.
 * @example
 *
 * _.defer(function(stamp) {
 *   console.log(_.now() - stamp);
 * }, _.now());
 * // => Logs the number of milliseconds it took for the deferred invocation.
 */
var now = function() {
  return root.Date.now();
};

/**
 * Creates a debounced function that delays invoking `func` until after `wait`
 * milliseconds have elapsed since the last time the debounced function was
 * invoked. The debounced function comes with a `cancel` method to cancel
 * delayed `func` invocations and a `flush` method to immediately invoke them.
 * Provide `options` to indicate whether `func` should be invoked on the
 * leading and/or trailing edge of the `wait` timeout. The `func` is invoked
 * with the last arguments provided to the debounced function. Subsequent
 * calls to the debounced function return the result of the last `func`
 * invocation.
 *
 * **Note:** If `leading` and `trailing` options are `true`, `func` is
 * invoked on the trailing edge of the timeout only if the debounced function
 * is invoked more than once during the `wait` timeout.
 *
 * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
 * until to the next tick, similar to `setTimeout` with a timeout of `0`.
 *
 * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
 * for details over the differences between `_.debounce` and `_.throttle`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to debounce.
 * @param {number} [wait=0] The number of milliseconds to delay.
 * @param {Object} [options={}] The options object.
 * @param {boolean} [options.leading=false]
 *  Specify invoking on the leading edge of the timeout.
 * @param {number} [options.maxWait]
 *  The maximum time `func` is allowed to be delayed before it's invoked.
 * @param {boolean} [options.trailing=true]
 *  Specify invoking on the trailing edge of the timeout.
 * @returns {Function} Returns the new debounced function.
 * @example
 *
 * // Avoid costly calculations while the window size is in flux.
 * jQuery(window).on('resize', _.debounce(calculateLayout, 150));
 *
 * // Invoke `sendMail` when clicked, debouncing subsequent calls.
 * jQuery(element).on('click', _.debounce(sendMail, 300, {
 *   'leading': true,
 *   'trailing': false
 * }));
 *
 * // Ensure `batchLog` is invoked once after 1 second of debounced calls.
 * var debounced = _.debounce(batchLog, 250, { 'maxWait': 1000 });
 * var source = new EventSource('/stream');
 * jQuery(source).on('message', debounced);
 *
 * // Cancel the trailing debounced invocation.
 * jQuery(window).on('popstate', debounced.cancel);
 */
function debounce(func, wait, options) {
  var lastArgs,
      lastThis,
      maxWait,
      result,
      timerId,
      lastCallTime,
      lastInvokeTime = 0,
      leading = false,
      maxing = false,
      trailing = true;

  if (typeof func != 'function') {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  wait = toNumber(wait) || 0;
  if (isObject(options)) {
    leading = !!options.leading;
    maxing = 'maxWait' in options;
    maxWait = maxing ? nativeMax(toNumber(options.maxWait) || 0, wait) : maxWait;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }

  function invokeFunc(time) {
    var args = lastArgs,
        thisArg = lastThis;

    lastArgs = lastThis = undefined;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }

  function leadingEdge(time) {
    // Reset any `maxWait` timer.
    lastInvokeTime = time;
    // Start the timer for the trailing edge.
    timerId = setTimeout(timerExpired, wait);
    // Invoke the leading edge.
    return leading ? invokeFunc(time) : result;
  }

  function remainingWait(time) {
    var timeSinceLastCall = time - lastCallTime,
        timeSinceLastInvoke = time - lastInvokeTime,
        result = wait - timeSinceLastCall;

    return maxing ? nativeMin(result, maxWait - timeSinceLastInvoke) : result;
  }

  function shouldInvoke(time) {
    var timeSinceLastCall = time - lastCallTime,
        timeSinceLastInvoke = time - lastInvokeTime;

    // Either this is the first call, activity has stopped and we're at the
    // trailing edge, the system time has gone backwards and we're treating
    // it as the trailing edge, or we've hit the `maxWait` limit.
    return (lastCallTime === undefined || (timeSinceLastCall >= wait) ||
      (timeSinceLastCall < 0) || (maxing && timeSinceLastInvoke >= maxWait));
  }

  function timerExpired() {
    var time = now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    // Restart the timer.
    timerId = setTimeout(timerExpired, remainingWait(time));
  }

  function trailingEdge(time) {
    timerId = undefined;

    // Only invoke if we have `lastArgs` which means `func` has been
    // debounced at least once.
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = lastThis = undefined;
    return result;
  }

  function cancel() {
    if (timerId !== undefined) {
      clearTimeout(timerId);
    }
    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timerId = undefined;
  }

  function flush() {
    return timerId === undefined ? result : trailingEdge(now());
  }

  function debounced() {
    var time = now(),
        isInvoking = shouldInvoke(time);

    lastArgs = arguments;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timerId === undefined) {
        return leadingEdge(lastCallTime);
      }
      if (maxing) {
        // Handle invocations in a tight loop.
        timerId = setTimeout(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }
    if (timerId === undefined) {
      timerId = setTimeout(timerExpired, wait);
    }
    return result;
  }
  debounced.cancel = cancel;
  debounced.flush = flush;
  return debounced;
}

/**
 * Creates a throttled function that only invokes `func` at most once per
 * every `wait` milliseconds. The throttled function comes with a `cancel`
 * method to cancel delayed `func` invocations and a `flush` method to
 * immediately invoke them. Provide `options` to indicate whether `func`
 * should be invoked on the leading and/or trailing edge of the `wait`
 * timeout. The `func` is invoked with the last arguments provided to the
 * throttled function. Subsequent calls to the throttled function return the
 * result of the last `func` invocation.
 *
 * **Note:** If `leading` and `trailing` options are `true`, `func` is
 * invoked on the trailing edge of the timeout only if the throttled function
 * is invoked more than once during the `wait` timeout.
 *
 * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
 * until to the next tick, similar to `setTimeout` with a timeout of `0`.
 *
 * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
 * for details over the differences between `_.throttle` and `_.debounce`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to throttle.
 * @param {number} [wait=0] The number of milliseconds to throttle invocations to.
 * @param {Object} [options={}] The options object.
 * @param {boolean} [options.leading=true]
 *  Specify invoking on the leading edge of the timeout.
 * @param {boolean} [options.trailing=true]
 *  Specify invoking on the trailing edge of the timeout.
 * @returns {Function} Returns the new throttled function.
 * @example
 *
 * // Avoid excessively updating the position while scrolling.
 * jQuery(window).on('scroll', _.throttle(updatePosition, 100));
 *
 * // Invoke `renewToken` when the click event is fired, but not more than once every 5 minutes.
 * var throttled = _.throttle(renewToken, 300000, { 'trailing': false });
 * jQuery(element).on('click', throttled);
 *
 * // Cancel the trailing throttled invocation.
 * jQuery(window).on('popstate', throttled.cancel);
 */
function throttle(func, wait, options) {
  var leading = true,
      trailing = true;

  if (typeof func != 'function') {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  if (isObject(options)) {
    leading = 'leading' in options ? !!options.leading : leading;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }
  return debounce(func, wait, {
    'leading': leading,
    'maxWait': wait,
    'trailing': trailing
  });
}

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */
function isObject(value) {
  var type = typeof value;
  return !!value && (type == 'object' || type == 'function');
}

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return !!value && typeof value == 'object';
}

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol(value) {
  return typeof value == 'symbol' ||
    (isObjectLike(value) && objectToString.call(value) == symbolTag);
}

/**
 * Converts `value` to a number.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {number} Returns the number.
 * @example
 *
 * _.toNumber(3.2);
 * // => 3.2
 *
 * _.toNumber(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toNumber(Infinity);
 * // => Infinity
 *
 * _.toNumber('3.2');
 * // => 3.2
 */
function toNumber(value) {
  if (typeof value == 'number') {
    return value;
  }
  if (isSymbol(value)) {
    return NAN;
  }
  if (isObject(value)) {
    var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
    value = isObject(other) ? (other + '') : other;
  }
  if (typeof value != 'string') {
    return value === 0 ? value : +value;
  }
  value = value.replace(reTrim, '');
  var isBinary = reIsBinary.test(value);
  return (isBinary || reIsOctal.test(value))
    ? freeParseInt(value.slice(2), isBinary ? 2 : 8)
    : (reIsBadHex.test(value) ? NAN : +value);
}

var lodash_throttle = throttle;

var Viewer = { render: function render() {
    var _vm = this;var _h = _vm.$createElement;var _c = _vm._self._c || _h;return _c('div', { directives: [{ name: "show", rawName: "v-show", value: _vm.visible, expression: "visible" }], staticClass: "td-viewer", on: { "mousewheel": _vm.onMouseWheel } }, [_c('div', { staticClass: "td-viewer__header" }, [_c('div', { staticClass: "td-viewer__operate" }, [_vm.canFullscreen ? [_c('a', { attrs: { "href": "javascript:;", "title": "最小化" }, on: { "click": function click($event) {
          return _vm.$emit('minimize');
        } } }, [_c('i', { staticClass: "td-icon-minimize" })]), _vm._v(" "), !_vm.fullscreen ? _c('a', { attrs: { "href": "javascript:;", "title": "最大化" }, on: { "click": function click($event) {
          return _vm.$emit('fullscreen');
        } } }, [_c('i', { staticClass: "td-icon-maximize" })]) : _c('a', { attrs: { "href": "javascript:;" } }, [_c('i', { staticClass: "td-icon-restore", attrs: { "title": "还原" }, on: { "click": function click($event) {
          return _vm.$emit('restore');
        } } })])] : _vm._e(), _vm._v(" "), _vm._m(0)], 2)]), _vm._v(" "), _c('div', { ref: "container", staticClass: "td-viewer__body" }, [_c('img', { directives: [{ name: "show", rawName: "v-show", value: !_vm.isLoading, expression: "!isLoading" }], ref: "image", class: { 'is-origin-ratio': _vm.isShowingActualRatio }, style: _vm.imageStyle, attrs: { "draggable": "true", "alt": "", "src": _vm.currentShowingImg }, on: { "load": _vm.onImageLoaded, "dragstart": _vm.onDragStart, "dragover": _vm.onDragOver } }), _vm._v(" "), _vm.isSrcPropArray ? [_c('span', { staticClass: "td-viewer__button td-viewer__button--left", class: { 'is-disabled': _vm.isArrowDisabled.left }, on: { "click": function click($event) {
          return _vm.changeImage(-1);
        } } }, [_c('i', { staticClass: "td-icon-arrow-left" })]), _vm._v(" "), _c('span', { staticClass: "td-viewer__button td-viewer__button--right", class: { 'is-disabled': _vm.isArrowDisabled.right }, on: { "click": function click($event) {
          return _vm.changeImage(1);
        } } }, [_c('i', { staticClass: "td-icon-arrow-right" })])] : _vm._e(), _vm._v(" "), _vm.isLoading ? _c('div', { staticClass: "td-viewer__loading" }, [_c('i', { staticClass: "td-icon-loading" }), _vm._v(" "), _c('p', [_vm._v("图片加载中...")])]) : _vm._e()], 2), _vm._v(" "), _c('div', { staticClass: "td-viewer__footer" }, [_c('a', { attrs: { "href": "javacript:;", "title": _vm.buttonText.zoomIn }, on: { "click": _vm.zoomIn } }, [_c('i', { staticClass: "td-icon-magnify" })]), _vm._v(" "), _c('a', { attrs: { "href": "javacript:;", "title": _vm.buttonText.zoomOut }, on: { "click": _vm.zoomOut } }, [_c('i', { staticClass: "td-icon-minify" })]), _vm._v(" "), _vm.isShowingActualRatio ? _c('a', { attrs: { "href": "javacript:;", "title": _vm.buttonText.ratioOptimal }, on: { "click": _vm.showOptimalRatio } }, [_c('i', { staticClass: "td-icon-ratio-optimal" })]) : _c('a', { attrs: { "href": "javacript:;", "title": _vm.buttonText.ratioOptimal }, on: { "click": _vm.showActualRatio } }, [_c('i', { staticClass: "td-icon-ratio" })]), _vm._v(" "), _c('a', { attrs: { "href": "javacript:;", "title": _vm.buttonText.rotate }, on: { "click": _vm.rotate } }, [_c('i', { staticClass: "td-icon-refresh" })]), _vm._v(" "), _c('a', { attrs: { "href": "javascript:;", "title": _vm.buttonText.download }, on: { "click": function click($event) {
          return _vm.download(_vm.currentShowingImg);
        } } }, [_c('i', { staticClass: "td-icon-download" })])])]);
  }, staticRenderFns: [function () {
    var _vm = this;var _h = _vm.$createElement;var _c = _vm._self._c || _h;return _c('a', { attrs: { "href": "javascript:;" } }, [_c('i', { staticClass: "td-icon-close", attrs: { "title": "关闭" } })]);
  }], _scopeId: 'data-v-59204da6',
  name: NAMESPACE + '-viewer',
  props: {
    src: {
      type: [String, Array],
      require: true
    },
    maxScaleRatio: {
      type: Number,
      default: 5
    },
    minScaleRatio: {
      type: Number,
      default: 0.107374
    },
    visible: {
      type: Boolean,
      default: true
    },
    fullscreen: {
      type: Boolean,
      default: false
    },
    canFullscreen: {
      type: Boolean,
      default: true
    },
    customButtonText: {
      type: Object,
      default: function _default() {
        return {};
      }
    },
    useBrowserDownload: {
      type: Boolean,
      default: false
    }
  },
  data: function data() {
    return {
      isLoading: true,
      isShowingActualRatio: false,
      scaleRatio: 1,
      rotateDegree: 0,
      index: 0,
      dragPosition: {
        x: 0,
        y: 0
      },
      deltaPostion: {
        x: 0,
        y: 0
      },
      transparentImage: null,
      buttonText: {
        'zoomIn': '放大',
        'zoomOut': '缩小',
        'ratioOptimal': '最佳比例',
        'ratioOrigin': '实际大小',
        'rotate': '旋转',
        'download': '取回'
      }
    };
  },

  computed: {
    isSrcPropArray: function isSrcPropArray() {
      return Array.isArray(this.src);
    },
    imgCount: function imgCount() {
      return this.isSrcPropArray ? this.src.length : 1;
    },
    currentShowingImg: function currentShowingImg() {
      return this.isSrcPropArray ? this.src[this.index] : this.src;
    },
    isArrowDisabled: function isArrowDisabled() {
      return {
        left: this.index === 0,
        right: this.index === this.imgCount - 1
      };
    },
    imageStyle: function imageStyle() {
      var centerize = this.isShowingActualRatio ? 'translate(-50%, -50%)' : '';
      var scale = 'scale(' + this.scaleRatio + ')';
      var rotate = 'rotate(' + this.rotateDegree + 'deg)';
      var translate = 'translate(' + this.deltaPostion.x + 'px, ' + this.deltaPostion.y + 'px)';
      return 'transform: ' + centerize + ' ' + scale + ' ' + rotate + ' ' + translate + ';';
    }
  },
  mounted: function mounted() {
    var img = new Image();
    // 加载一个1*1 px的透明图片，替换默认预览图
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    this.transparentImage = img;
    this.buttonText = _extends({}, this.buttonText, this.customButtonText);
  },

  watch: {
    currentShowingImg: {
      immediate: true,
      handler: function handler() {
        this.isLoading = true;
      }
    }
  },
  methods: {
    showActualRatio: function showActualRatio() {
      this.isShowingActualRatio = true;
      this.scaleRatio = 1;
      this.resetDragPosition();
    },
    showOptimalRatio: function showOptimalRatio() {
      this.isShowingActualRatio = false;
      this.scaleRatio = 1;
      this.resetDragPosition();
    },
    zoomIn: function zoomIn() {
      var result = this.scaleRatio;
      result = result >= 1 ? result + 0.2 : result * 1.25;
      if (result <= this.maxScaleRatio) {
        this.scaleRatio = result;
      }
    },
    zoomOut: function zoomOut() {
      var result = this.scaleRatio;
      result = result >= 1.2 ? result - 0.2 : result * 0.8;
      if (result >= this.minScaleRatio) {
        this.scaleRatio = result;
      }
    },
    changeImage: function changeImage(delta) {
      var result = this.index + delta;
      if (result >= 0 && result < this.imgCount) {
        this.index = result;
        this.reset();
      }
    },
    rotate: function rotate() {
      this.rotateDegree = (this.rotateDegree + 90) % 360;
    },
    onMouseWheel: function onMouseWheel(e) {
      e.preventDefault();
      if (e.deltaY > 0) {
        this.zoomOut();
      } else {
        this.zoomIn();
      }
    },
    reset: function reset() {
      this.isLoading = true;
      this.scaleRatio = 1;
      this.rotateDegree = 0;
      this.resetDragPosition();
    },
    download: function download(imgUrl) {
      this.$emit('download', imgUrl);
      if (this.useBrowserDownload) {
        var image = new Image();
        // 解决跨域 canvas污染问题
        image.setAttribute('crossOrigin', 'anonymous');
        image.onload = function () {
          var canvas = document.createElement('canvas');
          canvas.width = image.width;
          canvas.height = image.height;
          var context = canvas.getContext('2d');
          context.drawImage(image, 0, 0, image.width, image.height);
          var url = canvas.toDataURL('image/png'); // 图片转为base64格式
          var a = document.createElement('a');
          var e = new MouseEvent('click');
          a.download = '图片';
          a.href = url;
          a.dispatchEvent(e);
        };
        // imgUrl即为要下载图片的路径
        image.src = imgUrl + '?time=' + new Date().valueOf(); // 加时间戳，防止重用缓存，会导致跨域问题
      }
    },
    onImageLoaded: function onImageLoaded() {
      this.isLoading = false;
    },
    onDragStart: function onDragStart(e) {
      e.dataTransfer.setDragImage(this.transparentImage, 10, 10);
      this.dragPosition.x = e.clientX;
      this.dragPosition.y = e.clientY;
    },

    onDragOver: lodash_throttle(function (e) {
      this.deltaPostion.x = this.deltaPostion.x + (e.clientX - this.dragPosition.x);
      this.deltaPostion.y = this.deltaPostion.y + (e.clientY - this.dragPosition.y);
      this.dragPosition.x = e.clientX;
      this.dragPosition.y = e.clientY;
    }, 10),
    onClose: function onClose() {
      this.visible = false;
    },
    resetDragPosition: function resetDragPosition() {
      this.deltaPostion = {
        x: 0,
        y: 0
      };
      this.dragPosition = {
        x: 0,
        y: 0
      };
    }
  }
};

Viewer.install = function (Vue) {
  Vue.component(Viewer.name, Viewer);
};

export default Viewer;
